import React, { useState, useEffect } from 'react';
import { ArrowUpDown, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip, XAxis } from 'recharts';
import { formatInTimeZone } from 'date-fns-tz';
import { ET_TIMEZONE } from './utils/marketUtils';
import TradeRecordDialog from './TradeRecordDialog';

const formatTime = (timestamp) => {
  if (!timestamp || isNaN(timestamp)) return '';
  return formatInTimeZone(new Date(timestamp), ET_TIMEZONE, 'HH:mm:ss');
};

const getArrowColor = (arrow, avgChangePercent) => {
  if (!arrow || arrow === '') return 'inherit';
  
  if (avgChangePercent >= 0) {
    switch(arrow) {
      case '↗': return '#166534'; // Dark green - positive & accelerating
      case '→': return '#22c55e'; // Green - positive & steady
      case '↘': return '#f59e0b'; // Orange - positive but decelerating
      default: return 'inherit';
    }
  } else {
    switch(arrow) {
      case '↗': return '#f97316'; // Orange - negative but improving
      case '→': return '#ef4444'; // Red - negative & steady
      case '↘': return '#dc2626'; // Dark red - negative & getting worse
      default: return 'inherit';
    }
  }
};

const getUniqueTicks = (data, interval) => {
  const seenIntervals = new Set();
  return data.filter(item => {
    const intervalStart = Math.floor(item.timestamp / interval) * interval;
    if (!seenIntervals.has(intervalStart)) {
      seenIntervals.add(intervalStart);
      return true;
    }
    return false;
  }).map(item => item.timestamp);
};

const cleanStockName = (name) => {
  const suffixesToRemove = [
    'Common Stock', 'Common Shares', 'Common Subordinate Voting Shares',
    'Class A Common Stock', 'Class B Common Stock', 'Class C Common Stock',
    'Class A', 'Class B', 'Class C', 'American Depositary Shares', 'ADS',
    'Inc', 'Inc.', 'Ltd.', 'Ordinary Share', 'Ordinary Shares',
    'IncOrdinary Shares', 'Subordinate Voting Shares',
    'Units representing Partner Interests',
    'Common Units representing Partner Interests',
    'Corporation', 'Corp.', 'Limited', ', plcOrdinary Shares', ',.', 
    'AGOrdinary Shares', 'DIOrdinary Shares', 'TechOrdinary Shares',
    'HoldingOrdinary Shares', 'plc', 'AG', 'DI', 'Tech', 'Holding'
  ];
  
  let cleanedName = name;
  suffixesToRemove.forEach(suffix => {
    cleanedName = cleanedName.replace(new RegExp(`\\s*\\b${suffix}\\b\\s*`, 'gi'), '');
  });
  
  cleanedName = cleanedName.replace(/,\s*$/, '');
  cleanedName = cleanedName.replace(/\s+/g, ' ');
  
  return cleanedName.trim();
};

const calculateDailyGain = (currentPrice, previousClose) => {
  return ((currentPrice - previousClose) / previousClose) * 100;
};

const CustomTooltip = ({ active, payload, label, previousClose }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dailyGain = calculateDailyGain(data.price, previousClose);
    return (
      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
        <p style={{ color: dailyGain >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
          {`Daily Gain: ${dailyGain >= 0 ? '+' : ''}${dailyGain.toFixed(2)}%`}
        </p>
        <p>{`Price: $${data.price.toFixed(2)}`}</p>
        <p>{`Time: ${formatTime(label)}`}</p>
      </div>
    );
  }
  return null;
};

export const StockTable = ({
  stocks,
  sortCriteria = [],
  onSort,
  marketStatus,
  priceHistoryMinutes = 90,
  pricePointIntervalSeconds = 30,
  xAxisTickIntervalMinutes = 5,
  avgChangeHistory = {},
  avgChangeByConfig = {},
  selectedConfig = { key: '24/8' },
  getAccelerationArrow,
  calculateStockScore,
  calculatePersonalizedAdjustment,
  userProfile,
  // New props for trade recording
  aiAnthropicResult,
  aiOpenaiResult,
  marketSentiment,
  marketIndices
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [tradeDialogOpen, setTradeDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Phase 2: Helper function to find AI result for a stock symbol
  const getAiResultForStock = (symbol) => {
    // Check Claude results first, then OpenAI
    const claudePick = aiAnthropicResult?.topPicks?.find(p => p.symbol === symbol);
    const gptPick = aiOpenaiResult?.topPicks?.find(p => p.symbol === symbol);

    // Prefer Claude if both have results, or return whichever is available
    if (claudePick?.matchesUserSuccessPattern || claudePick?.matchesUserAvoidPattern) {
      return claudePick;
    }
    if (gptPick?.matchesUserSuccessPattern || gptPick?.matchesUserAvoidPattern) {
      return gptPick;
    }
    // Return any pick if no pattern match
    return claudePick || gptPick || null;
  };

  // Phase 2: Helper function to get pattern sort priority
  // Priority: 1 = success pattern (top), 2 = no match (middle), 3 = avoid pattern (bottom/warning)
  const getPatternSortPriority = (symbol) => {
    const aiResult = getAiResultForStock(symbol);
    if (!aiResult) return 2; // No AI result = neutral
    if (aiResult.matchesUserSuccessPattern) return 1; // Success = top
    if (aiResult.matchesUserAvoidPattern) return 3; // Avoid = bottom (warning zone)
    return 2; // No pattern match = neutral
  };

  // Handle trade record dialog
  const handleOpenTradeDialog = (stock, action) => {
    // Enrich stock with calculated values
    const stockAvgChange = avgChangeByConfig[stock.symbol]?.[selectedConfig.key] ?? stock.avgChangePercent ?? 0;
    const arrow = getAccelerationArrow ? getAccelerationArrow(avgChangeHistory[stock.symbol]) : '';
    const scoreData = calculateStockScore ? calculateStockScore(stock.dailyGainPercentage || 0, stockAvgChange, arrow) : null;

    const enrichedStock = {
      ...stock,
      avgChangePercentage: stockAvgChange,
      accelerationArrow: arrow,
      score: scoreData?.points || 0
    };

    setSelectedStock(enrichedStock);
    setSelectedAction(action);
    setTradeDialogOpen(true);
  };

  const handleCloseTradeDialog = () => {
    setTradeDialogOpen(false);
    setSelectedStock(null);
    setSelectedAction(null);
  };

  const handleSaveTradeRecord = (tradeRecord) => {
    console.log('Trade record saved:', tradeRecord);
    // Parent component can handle this if needed via callback
  };

  // Find sort priority for a field (1, 2, 3 or null)
  const getSortPriority = (field) => {
    const index = sortCriteria.findIndex(c => c.field === field);
    return index >= 0 ? index + 1 : null;
  };

  // Get sort order for a field
  const getSortOrder = (field) => {
    const criterion = sortCriteria.find(c => c.field === field);
    return criterion ? criterion.order : null;
  };

  // Get header background color based on sort priority
  const getHeaderStyle = (field) => {
    const priority = getSortPriority(field);
    const baseStyle = {
      textAlign: 'right',
      padding: '0.5rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    };

    if (!priority) {
      return { ...baseStyle, backgroundColor: 'transparent' };
    }

    // Different shades for different priorities
    const colors = {
      1: '#dbeafe', // Primary sort - light blue
      2: '#e0f2fe', // Secondary sort - lighter blue
      3: '#f0f9ff'  // Tertiary sort - very light blue
    };

    return {
      ...baseStyle,
      backgroundColor: colors[priority] || 'transparent',
      borderBottom: priority === 1 ? '2px solid #3b82f6' : '1px solid #93c5fd'
    };
  };

  // Render sort icon with priority number
  const renderSortIcon = (field) => {
    const priority = getSortPriority(field);
    const order = getSortOrder(field);

    if (!priority) return <ArrowUpDown size={16} style={{ opacity: 0.4 }} />;

    const priorityBadge = sortCriteria.length > 1 ? (
      <span style={{
        fontSize: '10px',
        marginLeft: '4px',
        backgroundColor: priority === 1 ? '#2563eb' : '#60a5fa',
        color: 'white',
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold'
      }}>
        {priority}
      </span>
    ) : null;

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {order === 'asc' ? (
          <TrendingUp size={18} style={{ color: '#2563eb' }} />
        ) : (
          <TrendingDown size={18} style={{ color: '#2563eb' }} />
        )}
        {priorityBadge}
      </span>
    );
  };

  // Handle click with shift detection
  const handleHeaderClick = (field, event) => {
    onSort(field, event.shiftKey);
  };

  const isMarketOpen = marketStatus && (marketStatus.market === 'open' || marketStatus.market === 'extended-hours');

  const filterRecentPriceHistory = (priceHistory) => {
    const currentTime = Date.now();
    return priceHistory.filter(point => (currentTime - point.timestamp) <= priceHistoryMinutes * 60 * 1000);
  };

  const renderMobileTable = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '3%' }}>Symbol</th>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '94%' }}>Chart</th>
          <th
            style={{ ...getHeaderStyle('avgChangePercent'), width: '3%' }}
            onClick={(e) => handleHeaderClick('avgChangePercent', e)}
            title="Click to sort, Shift+Click to add secondary sort"
          >
            Avg % {renderSortIcon('avgChangePercent')}
          </th>
        </tr>
      </thead>
      <tbody>
        {stocks.map((stock) => (
          <tr key={stock.symbol} style={{ borderBottom: '1px solid #e5e7eb' }}>
            <td style={{ padding: '0.5rem', verticalAlign: 'top', fontSize: '0.8rem' }}>
              <div>{stock.symbol}</div>
            </td>
            <td style={{ padding: '0.5rem' }}>
              {stock.priceHistory && stock.priceHistory.length > 0 ? (
                <div style={{ width: '100%', height: '100px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filterRecentPriceHistory(stock.priceHistory)}>
                      <Line type="linear" dataKey="price" stroke={stock.avgChangePercent >= 0 ? "#22c55e" : "#ef4444"} strokeWidth={2} dot={false} />
                      <YAxis domain={['dataMin', 'dataMax']} hide />
                      <XAxis dataKey="timestamp" tickFormatter={formatTime} hide />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px', backgroundColor: '#f3f4f6', fontSize: '0.8rem' }}>
                  {!isMarketOpen ? 'Market closed. ' : ''}Collecting data...
                </div>
              )}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', verticalAlign: 'top', fontSize: '0.8rem' }}>
              <div style={{ color: stock.avgChangePercent >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                {stock.avgChangePercent >= 0 ? '+' : ''}{stock.avgChangePercent.toFixed(2)}%
                {getAccelerationArrow && (() => {
                  const arrow = getAccelerationArrow(avgChangeHistory[stock.symbol]);
                  return arrow ? (
                    <span style={{ 
                      marginLeft: '6px', 
                      fontSize: '1.5em',
                      fontWeight: 'bold',
                      color: getArrowColor(arrow, stock.avgChangePercent)
                    }}>
                      {arrow}
                    </span>
                  ) : null;
                })()}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderDesktopTable = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '5%' }}>Symbol</th>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '10%' }}>Name</th>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '65%' }}>Recent Price Chart</th>
          <th
            style={getHeaderStyle('gapPercent')}
            onClick={(e) => handleHeaderClick('gapPercent', e)}
            title="Gap % = (Today Open - Prev Close) / Prev Close. Click to sort."
          >
            Gap % {renderSortIcon('gapPercent')}
          </th>
          <th style={getHeaderStyle('score')} onClick={(e) => handleHeaderClick('score', e)} title="Click to sort, Shift+Click to add secondary sort">
            Score {renderSortIcon('score')}
          </th>
          <th style={getHeaderStyle('claudeRating')} onClick={(e) => handleHeaderClick('claudeRating', e)} title="Click to sort by Claude rating, Shift+Click to add secondary sort">
            Claude {renderSortIcon('claudeRating')}
          </th>
          <th style={getHeaderStyle('gptRating')} onClick={(e) => handleHeaderClick('gptRating', e)} title="Click to sort by GPT rating, Shift+Click to add secondary sort">
            GPT {renderSortIcon('gptRating')}
          </th>
          <th style={getHeaderStyle('pattern')} onClick={(e) => handleHeaderClick('pattern', e)} title="Click to sort by pattern match priority">
            Pattern {renderSortIcon('pattern')}
          </th>
          <th style={getHeaderStyle('avgChangePercent')} onClick={(e) => handleHeaderClick('avgChangePercent', e)} title="Click to sort, Shift+Click to add secondary sort">
            Avg Change % {renderSortIcon('avgChangePercent')}
          </th>
          <th style={getHeaderStyle('dailyGainPercentage')} onClick={(e) => handleHeaderClick('dailyGainPercentage', e)} title="Click to sort, Shift+Click to add secondary sort">
            Daily Gain % {renderSortIcon('dailyGainPercentage')}
          </th>
          <th style={{ textAlign: 'center', padding: '0.5rem', width: '10%' }}>
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {stocks.map((stock) => {
          // Get config-specific avgChange for this stock
          const stockAvgChange = avgChangeByConfig[stock.symbol]?.[selectedConfig.key] ?? stock.avgChangePercent ?? 0;

          return (
          <tr key={stock.symbol} style={{ borderBottom: '1px solid #e5e7eb' }}>
            <td style={{ padding: '0.5rem', width: '5%', verticalAlign: 'top' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {stock.dailyGainPercentage >= 0 ? (
                  <TrendingUp size={16} style={{ marginRight: '0.5rem', color: '#22c55e' }} />
                ) : (
                  <TrendingDown size={16} style={{ marginRight: '0.5rem', color: '#ef4444' }} />
                )}
                <div style={{
                  width: '2rem',
                  height: '2rem',
                  backgroundColor: stock.dailyGainPercentage >= 0 ? '#22c55e' : '#ef4444',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  marginRight: '0.5rem'
                }}>
                  {stock.symbol.slice(0, 1)}
                </div>
                <div>{stock.symbol}</div>
              </div>
            </td>
            <td style={{ padding: '0.5rem', width: '10%', verticalAlign: 'top' }}>
              <div style={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%',
                fontSize: '0.9em',
                lineHeight: '1.2'
              }}>
                {cleanStockName(stock.name)}
                <div style={{
                  fontSize: '0.8em',
                  color: '#6b7280',
                  marginTop: '0.25rem'
                }}>
                  [ {stock.sicDescription} ]
                </div>
              </div>
            </td>
            <td style={{ padding: '0.5rem', width: '65%' }}>
              {stock.priceHistory && stock.priceHistory.length > 0 ? (
                <div style={{ width: '100%', height: '200px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filterRecentPriceHistory(stock.priceHistory)}>
                      <Line type="linear" dataKey="price" stroke={stockAvgChange >= 0 ? "#22c55e" : "#ef4444"} strokeWidth={2} dot={false} />
                      <YAxis 
                        domain={['dataMin', 'dataMax']} 
                        hide={false} 
                        padding={{ top: 10, bottom: 10 }} 
                      />
                      <XAxis 
                        dataKey="timestamp" 
                        tickFormatter={formatTime}
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        ticks={getUniqueTicks(filterRecentPriceHistory(stock.priceHistory), xAxisTickIntervalMinutes * 60 * 1000)}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip content={<CustomTooltip previousClose={stock.previousClose} />} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', backgroundColor: '#f3f4f6' }}>
                  {!isMarketOpen ? 'Market is currently closed. ' : ''}Collecting price data...
                </div>
              )}
            </td>
            {/* Gap % column */}
            <td style={{ padding: '0.5rem', textAlign: 'right', verticalAlign: 'top' }}>
              {stock.gapPercent != null && Math.abs(stock.gapPercent) >= 1 ? (
                <div style={{
                  fontWeight: 'bold',
                  color: stock.gapPercent >= 0 ? '#22c55e' : '#ef4444'
                }}>
                  {stock.gapPercent > 0 ? '+' : ''}{stock.gapPercent.toFixed(2)}%
                </div>
              ) : (
                <span style={{ color: '#9ca3af' }}>-</span>
              )}
            </td>
            {/* Score column */}
            <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
              {calculateStockScore && (() => {
                const arrow = getAccelerationArrow ? getAccelerationArrow(avgChangeHistory[stock.symbol]) : '';
                const dailyGain = stock.dailyGainPercentage || 0;
                const scoreData = calculateStockScore(dailyGain, stockAvgChange, arrow);

                // Calculate individual points for tooltip
                const dailyGainPoints = dailyGain >= 10 ? 3 : dailyGain >= 5 ? 2 : dailyGain >= 0 ? 1 : 0;
                const avgChangePoints = stockAvgChange >= 2 ? 3 : stockAvgChange >= 1 ? 2 : stockAvgChange >= 0 ? 1 : 0;
                const accelPoints = arrow === '↗' ? 2 : arrow === '→' ? 1 : 0;

                // Phase 2: Learning Loop - Get AI result for this stock
                const aiResult = getAiResultForStock(stock.symbol);

                // Calculate personalized adjustment with AI result
                const personalization = calculatePersonalizedAdjustment
                  ? calculatePersonalizedAdjustment(userProfile, marketSentiment, aiResult)
                  : { adjustment: 0, reasons: [], hasPositiveMatch: false, hasNegativeMatch: false, matchedPattern: null, isSuccessPattern: false, isAvoidPattern: false };

                const finalPoints = Math.max(0, Math.min(11, scoreData.points + personalization.adjustment));

                // Get grade for final points (extended to handle 0-11)
                const getGrade = (pts) => {
                  if (pts >= 8) return { grade: 'A+', emoji: '🟢🟢', color: '#15803d' };
                  if (pts >= 6) return { grade: 'A', emoji: '🟢', color: '#22c55e' };
                  if (pts >= 4) return { grade: 'B', emoji: '🟡', color: '#eab308' };
                  if (pts >= 2) return { grade: 'C', emoji: '🟠', color: '#f97316' };
                  return { grade: 'D', emoji: '🔴', color: '#ef4444' };
                };

                const finalGrade = getGrade(finalPoints);

                // Build tooltip text
                let tooltipText = `Score: ${finalGrade.grade} (${personalization.adjustment !== 0 ? `${scoreData.points}${personalization.adjustment > 0 ? '+' : ''}${personalization.adjustment}=${finalPoints}` : `${scoreData.points}/8`})\n` +
                  `─────────────────\n` +
                  `Base Score: ${scoreData.points}/8\n` +
                  `  Daily Gain: ${dailyGain >= 0 ? '+' : ''}${dailyGain.toFixed(2)}% → ${dailyGainPoints}pt\n` +
                  `  Avg Change: ${stockAvgChange >= 0 ? '+' : ''}${stockAvgChange.toFixed(2)}% → ${avgChangePoints}pt\n` +
                  `  Acceleration: ${arrow || 'N/A'} → ${accelPoints}pt`;

                if (personalization.adjustment !== 0) {
                  tooltipText += `\n─────────────────\n` +
                    `Personalization: ${personalization.adjustment > 0 ? '+' : ''}${personalization.adjustment}\n`;
                  personalization.reasons.forEach(r => {
                    tooltipText += `  ${r.type === 'positive' ? '✨' : '⚠️'} ${r.text}\n`;
                  });
                  tooltipText += `─────────────────\n` +
                    `Final Score: ${finalPoints}`;
                }

                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                      cursor: 'help'
                    }}
                    title={tooltipText}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <span style={{ fontSize: '1.2em' }}>{finalGrade.emoji}</span>
                      {/* Phase 2: Personalization icons based on AI pattern match */}
                      {personalization.isSuccessPattern && (
                        <span style={{ fontSize: '0.8em' }}>✨</span>
                      )}
                      {personalization.isAvoidPattern && (
                        <span style={{ fontSize: '0.8em' }}>⚠️</span>
                      )}
                    </div>
                    <span style={{
                      fontWeight: 'bold',
                      color: finalGrade.color,
                      fontSize: '0.9em'
                    }}>
                      {finalGrade.grade}
                    </span>
                    <span style={{
                      fontSize: '0.7em',
                      color: '#6b7280'
                    }}>
                      ({personalization.adjustment !== 0 ? `${scoreData.points}${personalization.adjustment > 0 ? '+' : ''}${personalization.adjustment}` : `${scoreData.points}/8`})
                    </span>
                  </div>
                );
              })()}
            </td>
            {/* Claude rating column */}
            <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
              {(() => {
                const claudePick = aiAnthropicResult?.topPicks?.find(p => p.symbol === stock.symbol);
                if (!claudePick || !claudePick.rating) {
                  return <span style={{ color: '#d1d5db' }}>-</span>;
                }
                const stars = Math.min(claudePick.rating, 3);
                return (
                  <span
                    title={`Claude: ${claudePick.rating}/3${claudePick.pattern ? ' — ' + claudePick.pattern : ''}`}
                    style={{ cursor: 'help', fontSize: '0.9em' }}
                  >
                    {'⭐'.repeat(stars)}
                  </span>
                );
              })()}
            </td>
            {/* GPT rating column */}
            <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
              {(() => {
                const gptPick = aiOpenaiResult?.topPicks?.find(p => p.symbol === stock.symbol);
                if (!gptPick || !gptPick.rating) {
                  return <span style={{ color: '#d1d5db' }}>-</span>;
                }
                const stars = Math.min(gptPick.rating, 3);
                return (
                  <span
                    title={`GPT: ${gptPick.rating}/3${gptPick.pattern ? ' — ' + gptPick.pattern : ''}`}
                    style={{ cursor: 'help', fontSize: '0.9em' }}
                  >
                    {'⭐'.repeat(stars)}
                  </span>
                );
              })()}
            </td>
            {/* Pattern column */}
            <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
              {(() => {
                const aiResult = getAiResultForStock(stock.symbol);

                if (!aiResult) {
                  return <span style={{ color: '#9ca3af' }}>-</span>;
                }

                const isSuccess = aiResult.matchesUserSuccessPattern;
                const isAvoid = aiResult.matchesUserAvoidPattern;
                const patternName = isSuccess
                  ? aiResult.userSuccessPatternName
                  : isAvoid
                    ? aiResult.userAvoidPatternName
                    : null;

                if (!patternName) {
                  return <span style={{ color: '#9ca3af' }}>-</span>;
                }

                // Truncate long pattern names
                const displayName = patternName.length > 12
                  ? patternName.substring(0, 10) + '...'
                  : patternName;

                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      cursor: 'help'
                    }}
                    title={`${isSuccess ? '✨ Proven Pattern' : '⚠️ Avoid Pattern'}: ${patternName}`}
                  >
                    <span style={{ fontSize: '0.9em' }}>
                      {isSuccess ? '✨' : '⚠️'}
                    </span>
                    <span style={{
                      fontSize: '0.8em',
                      color: isSuccess ? '#16a34a' : '#dc2626',
                      fontWeight: '500'
                    }}>
                      {displayName}
                    </span>
                  </div>
                );
              })()}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', verticalAlign: 'top' }}>
              <div style={{ color: stockAvgChange >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                {stockAvgChange >= 0 ? '+' : ''}{stockAvgChange.toFixed(2)}%
                {getAccelerationArrow && (() => {
                  const arrow = getAccelerationArrow(avgChangeHistory[stock.symbol]);
                  return arrow ? (
                    <span style={{
                      marginLeft: '4px',
                      fontSize: '0.9em',
                      color: getArrowColor(arrow, stockAvgChange)
                    }}>
                      {arrow}
                    </span>
                  ) : null;
                })()}
              </div>
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', verticalAlign: 'top' }}>
              <div style={{ color: stock.dailyGainPercentage >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                {stock.dailyGainPercentage >= 0 ? '+' : ''}{stock.dailyGainPercentage.toFixed(2)}%
              </div>
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                  onClick={() => handleOpenTradeDialog(stock, 'entry')}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#16a34a'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#22c55e'}
                  title="Record entry trade"
                >
                  Entry
                </button>
                <button
                  onClick={() => handleOpenTradeDialog(stock, 'exit')}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                  title="Record exit trade"
                >
                  Exit
                </button>
              </div>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      {isMobile ? renderMobileTable() : renderDesktopTable()}

      {/* Trade Record Dialog */}
      {tradeDialogOpen && selectedStock && (
        <TradeRecordDialog
          stock={selectedStock}
          action={selectedAction}
          marketContext={{
            status: marketStatus?.market,
            sentiment: marketSentiment,
            indices: marketIndices?.reduce((acc, idx) => {
              acc[idx.symbol] = idx.change || 0;
              return acc;
            }, {}) || {}
          }}
          aiAnthropicResult={aiAnthropicResult}
          aiOpenaiResult={aiOpenaiResult}
          onClose={handleCloseTradeDialog}
          onSave={handleSaveTradeRecord}
        />
      )}
    </>
  );
};

export default StockTable;
